import { useId, useMemo, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { CategoryIcon, type CategoryIconInput } from '../../platform/icons/CategoryIcon'
import type { LocalDate } from '../../platform/localDate'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Button } from '../../platform/primitives/Button'
import { averageCents } from '../../platform/money/totals'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Account } from '../accounts/useAccounts'
import { CATEGORIES_PATH } from '../categories/categoryPaths'
import type { CategoryListStatus } from '../categories/setCategoryLabel'
import { dayGroups } from '../transactions/dayGroups'
import { Pager } from '../transactions/Pager'
import { TransactionCards } from '../transactions/TransactionCards'
import { TransactionGrid } from '../transactions/TransactionGrid'
import { SCOPED_PAGE_SIZE } from '../transactions/useScopedTransactions'
import type { Txn } from '../transactions/transactionWire'
import type { TxnPage } from '../transactions/useTransactions'
import { DivergingBars } from './charts/DivergingBars'
import { periodTitle, periodWindow, priorPhrase, unitStart, type CashflowView, type Granularity } from './cashflowPeriod'
import { deltaPct, sharePercent, shortDelta } from './cashflowRules'
import type { DetailScope, Side } from './cashflowScope'
import type { ViewChange } from './CashflowScreen'
import type { Cashflow } from './cashflowWire'
import { PeriodHeader } from './PeriodHeader'
import { Tile } from './PeriodTiles'
import type { CashflowResult } from './useCashflow'
import './cashflow.css'

/** The page's name: loading while a merchant's own call is in flight, then its text (and a category's icon). */
export type DetailName =
  | { status: 'loading' }
  | { status: 'ready'; text: string; icon: CategoryIconInput | null }
  /** The name's own call failed: said as such, never shown as if it were a name. */
  | { status: 'error'; retry?: () => void }

/**
 * This category's monthly budget for the current month, in the server's own figures: what is left, or,
 * over, what was spent against the limit and the percentage the Budget screen shows. Nothing is derived.
 */
export type DetailBudget =
  | { kind: 'left'; remainingCents: number; currency: string }
  | { kind: 'over'; spentCents: number; limitCents: number; pct: number; currency: string }
  /** No envelope claims the category: the tile says so and links to where one is made. */
  | { kind: 'unset'; href: string }
  /** An envelope claims it but is weekly, paused or one of several: no monthly figure, a link to look. */
  | { kind: 'not_monthly'; href: string }
  | { kind: 'error'; retry?: () => void }

/** The overview's own `share_pct` for this row, and the period's whole expense it is a share of. */
export type DetailShare = { pct: number; expenseCents: number; currency: string }

export type DetailScreenProps = {
  scope: DetailScope
  side: Side
  name: DetailName
  view: CashflowView
  /** The server's day once it has said, the client's before. */
  today: LocalDate
  /** The scoped call's earliest, remembered by the container across a failed step. */
  earliest: LocalDate | null | undefined
  /** The scoped period: this row's totals and prior. Drives the stats. */
  period: CashflowResult
  /** The scoped series behind the chart. Null when there is no chart region: this row has no transactions at all, or the period call failed before it ever answered. */
  history: CashflowResult | null
  /** 'loading' while it is read, so its tile is held; null when there is no share to show; the screen also refuses one where a share means nothing. */
  share: DetailShare | 'loading' | null
  /** When `wantsBudget` the slot always renders a state; null only where the page has no budget tile. */
  budget?: DetailBudget | 'loading' | null
  /** Known at render: this page can carry a budget tile, so the skeleton reserves it. */
  wantsBudget?: boolean
  transactions: AsyncState<TxnPage> & { fetching: boolean }
  accounts: ReadonlyMap<string, Pick<Account, 'name' | 'mask'>>
  categories: ReadonlyMap<string, string>
  /** The categories list's state, for the row pills' naming (R7). */
  categoriesStatus: CategoryListStatus
  /** `/cashflow` at this page's view, built by the container. */
  crumbHref: string
  onViewChange: (next: CashflowView, how: ViewChange) => void
  onPage: (page: number) => void
  /** Given, each row's category pill opens the picker (the container owns it); absent, the pill is plain text. */
  onCategoryClick?: (t: Txn, opener: HTMLButtonElement) => void
  /** Given, each row also carries a merchant pill that opens the merchant picker (the container owns it); absent, no merchant pill. */
  onMerchantClick?: (t: Txn, opener: HTMLButtonElement) => void
  /** Drawn above the list: the No merchant page's "Set all N at once?" (the container decides whether there is one). */
  prompt?: ReactNode
}

const NOUN: Record<Granularity, string> = { month: 'month', quarter: 'quarter', year: 'year' }

/** The No merchant page's empty list: nothing is missing a merchant, which is the good outcome. */
export const NO_MERCHANT_EMPTY = 'Every transaction this period has a merchant.'

export const FLAGGED_NOTE =
  "More money came in than went out in a spending category. If these are transfers between your accounts or money you received, change the category's type to Transfer or Income."

// Success only: `cashflow` is a live surface, so `<Async>` throws on a stale state before this could read one.
const data = (s: AsyncState<Cashflow>): Cashflow | null => (s.status === 'success' ? s.data : null)

/** "Try again" calls the region's own retry, so one region's error never retries another. */
function withRetry(s: AsyncState<Cashflow>, retry: () => void): AsyncState<Cashflow> {
  return s.status === 'error' ? { ...s, recovery: { label: 'Try again', onAct: retry } } : s
}

/** A category or group whose scoped expense is negative is collecting income or transfers: it is misfiled. */
function flaggedLink(scope: DetailScope, d: Cashflow | null): string | null {
  if (d === null || d.totals.expenseCents >= 0) return null
  if (scope.kind !== 'category' && scope.kind !== 'group') return null
  return `${CATEGORIES_PATH}/${encodeURIComponent(scope.id)}`
}

/** The grid class for n tiles: two is the default, three to five have their own, none leaves an orphan. */
const tilesClass = (n: number): string => (n === 3 ? ' cf-tiles--three' : n === 4 ? ' cf-tiles--four' : n === 5 ? ' cf-tiles--five' : '')

const TILE_KEYS = ['amount', 'share', 'average', 'budget', 'count'] as const

/** Budget slot: every state is a tile, so a failed or missing budget is said, never silently absent. */
function BudgetTile({ budget }: { budget: DetailBudget | 'loading' }) {
  if (budget === 'loading') return <Tile label="Budget" figure={<span className="cf-tile__figure"><Skeleton height={22} width={60} /></span>} sub={null} />
  switch (budget.kind) {
    case 'left':
      return (
        <Tile
          label="Budget remaining"
          figure={<span className="cf-tile__figure"><Money cents={budget.remainingCents} currency={budget.currency} display="whole" /></span>}
          sub="this month"
        />
      )
    case 'over':
      return (
        <Tile
          label="Over budget"
          figure={<span className="cf-tile__figure"><Money cents={budget.spentCents} currency={budget.currency} display="whole" /></span>}
          sub={
            <>
              {budget.pct}% of <Money cents={budget.limitCents} currency={budget.currency} display="whole" /> this month
            </>
          }
        />
      )
    case 'unset':
      return (
        <Tile
          label="Budget"
          figure={<span className="cf-tile__figure cf-tile__figure--pair">Not set</span>}
          sub={<InlineLink to={budget.href}>Set up a budget</InlineLink>}
        />
      )
    case 'not_monthly':
      return (
        <Tile
          label="Budget"
          figure={<span className="cf-tile__figure cf-tile__figure--pair">No monthly budget</span>}
          sub={<InlineLink to={budget.href}>View budgets</InlineLink>}
        />
      )
    case 'error':
      return (
        <Tile
          label="Budget"
          figure={<span className="cf-tile__figure cf-tile__figure--pair">Couldn&apos;t load</span>}
          sub={budget.retry === undefined ? null : <Button size="page" variant="outline" onClick={budget.retry}>Try again</Button>}
        />
      )
  }
}

/** The loading shape, with as many tiles as the page will settle to, so nothing shifts when it lands. */
function StatsSkeleton({ tiles }: { tiles: number }) {
  const bp = useBreakpoint()
  return (
    <div className={`cf-tiles${tilesClass(tiles)} cf-tiles--${bp}`} aria-hidden="true">
      {TILE_KEYS.slice(0, tiles).map((k) => (
        <Panel key={k} className="cf-tile">
          <Skeleton height={10} width="45%" />
          <Skeleton height={26} width="60%" />
          <Skeleton height={10} width="40%" />
        </Panel>
      ))}
    </div>
  )
}

function ChartRegion({ title, children }: { title: string; children: (headingId: string) => ReactNode }) {
  const headingId = useId()
  return (
    <Panel className="cf-panel" role="region" aria-labelledby={headingId}>
      <h3 id={headingId} className="cf-panel__title">
        {title}
      </h3>
      {children(headingId)}
    </Panel>
  )
}

/**
 * One row of `/cashflow` opened on its own (the drill-down): the crumb back,
 * the name, the same period header, a one-sided chart of this row's own
 * series, three stats, and the transactions behind the figure, by day.
 *
 * Presentational: the container owns the calls, the URL, the entitlement and
 * the analytics. Every figure is the server's — the scoped call's `totals`
 * and `prior`, the overview's `share_pct`, the list's `total`; the only
 * arithmetic is `deltaPct`.
 *
 * The page name is an `<h2>`, not the design's h1: the TopBar's breadcrumb
 * holds the document's one `<h1>` (#32, `TopBar.tsx`), as on `/categories/:id`.
 * For the same reason the in-page crumb's landmark is "Cashflow breadcrumb",
 * so two navigations are never both called "Breadcrumb".
 *
 * Three regions, each with its own `<Async>` and Try again (a live surface:
 * four states): the scoped period (stats and the flagged note), the history
 * (chart) and the list. A failed list or chart keeps the figure.
 */
export function CashflowDetailScreen({
  scope,
  side,
  name,
  view,
  today,
  earliest: knownEarliest,
  period,
  history,
  share,
  budget = null,
  wantsBudget = false,
  transactions,
  accounts,
  categories,
  categoriesStatus,
  crumbHref,
  onViewChange,
  onPage,
  onCategoryClick,
  onMerchantClick,
  prompt = null,
}: DetailScreenProps) {
  const bp = useBreakpoint()
  const phone = bp === 'phone'
  // A transaction opened from here goes Back to exactly this page, filters and all.
  const loc = useLocation()
  const backFrom = loc.pathname + loc.search
  const p = data(period.state)
  const h = history === null ? null : data(history.state)
  const asOf = p?.window.today ?? today
  const earliest = p !== null ? p.window.earliest : (knownEarliest ?? h?.window.earliest ?? null)
  const { partial } = periodWindow(view.period, view.granularity, asOf)
  const g = view.granularity
  const label = side === 'in' ? 'Received' : 'Spent'
  const flagLink = flaggedLink(scope, p)
  // A share means something only for a spending row that is not flagged (a negative share is meaningless).
  // A page that can carry a share: the skeleton draws its tile before the share is known.
  const shareScope = side === 'out' && (scope.kind === 'category' || scope.kind === 'group')
  const showShare = shareScope && share !== null && flagLink === null
  const title = periodTitle(view.period, g)
  const nameText = name.status === 'ready' ? name.text : null
  const emptyList = scope.kind === 'no_merchant' ? NO_MERCHANT_EMPTY : `Nothing in ${title}.`

  const listState: AsyncState<TxnPage> =
    transactions.status === 'success' && transactions.data.total === 0 ? { status: 'empty' } : transactions
  const total = transactions.status === 'success' || transactions.status === 'stale' ? transactions.data.total : null

  const drawn = useMemo(() => {
    const periods = h?.periods ?? []
    const from = h?.window.earliest ?? null
    if (from === null) return periods
    const first = unitStart(from, g)
    return periods.filter((x) => x.start >= first)
  }, [h, g])

  return (
    <div className="cf-page cf-detail">
      <nav aria-label="Cashflow breadcrumb" className="cf-detail__crumb">
        <InlineLink to={crumbHref}>Cashflow</InlineLink>
        {name.status !== 'error' && (
          <>
            <span aria-hidden="true"> / </span>
            <span className="cf-detail__crumb-here">
              {nameText ?? <span className="skeleton cf-detail__skel cf-detail__skel--crumb" aria-hidden="true" />}
            </span>
          </>
        )}
      </nav>
      <div className="cf-detail__head">
        <h2 className="cf-detail__title">
          {name.status === 'ready' ? (
            <>
              {name.icon !== null && (
                <span className="cf-detail__icon" aria-hidden="true">
                  <CategoryIcon category={name.icon} />
                </span>
              )}
              {name.text}
            </>
          ) : name.status === 'error' ? (
            "Couldn't load the name"
          ) : (
            <>
              <span className="sr-only">Loading name</span>
              <span className="skeleton cf-detail__skel cf-detail__skel--title" aria-hidden="true" />
            </>
          )}
        </h2>
        {name.status === 'error' && name.retry !== undefined && (
          // On the canvas beside the period arrows, so the page hit target as they take.
          <Button size="page" variant="outline" onClick={name.retry}>
            Try again
          </Button>
        )}
        <PeriodHeader view={view} today={asOf} earliest={earliest} partial={partial} shared={p?.scope.shared ?? 0} onChange={onViewChange} />
      </div>

      {flagLink !== null && (
        <Panel className="cf-panel cf-detail__flag">
          <p className="cf-detail__flag-text">{FLAGGED_NOTE}</p>
          <InlineLink to={flagLink}>Change its type in Categories</InlineLink>
        </Panel>
      )}

      {history !== null && (
        <ChartRegion title={`${label} by ${NOUN[g]}`}>
          {(headingId) => (
            <Async
              surface="cashflow"
              state={withRetry(history.state, history.retry)}
              skeleton={<Skeleton height={160} />}
              empty={{ title: 'No history yet' }}
            >
              {(d) => (
                <DivergingBars
                  periods={drawn}
                  granularity={d.granularity}
                  currency={d.currency}
                  selected={view.period}
                  onSelect={(start, via) => onViewChange({ ...view, period: start }, via)}
                  // A deficit is the whole account's; one row has none.
                  deficits={NO_DEFICITS}
                  labelledBy={headingId}
                  side={side === 'in' ? 'income' : 'expense'}
                />
              )}
            </Async>
          )}
        </ChartRegion>
      )}

      <div className="cf-body" aria-busy={period.fetching && period.state.status === 'success' ? 'true' : undefined}>
        <Async
          surface="cashflow"
          state={withRetry(period.state, period.retry)}
          skeleton={<StatsSkeleton tiles={2 + (shareScope ? 1 : 0) + 1 + (wantsBudget ? 1 : 0)} />}
          empty={{ title: `Nothing in ${title}.` }}
        >
          {(d) => {
            const amount = side === 'in' ? d.totals.incomeCents : d.totals.expenseCents
            const before = d.prior === null ? null : side === 'in' ? d.prior.incomeCents : d.prior.expenseCents
            // A flagged row has no meaningful change to report ("Down 357%"): the note says what is wrong instead.
            const sub =
              d.prior === null || flagLink !== null ? null : shortDelta(deltaPct(amount, before), priorPhrase(d.prior, d.granularity))
            // The count of the list as it stands, stale included (rule 10); the slot is held while it loads.
            const average = total === null || total === 0 ? null : averageCents(amount, total)
            const averageSlot = average !== null || transactions.status === 'loading'
            const tiles = 2 + (showShare ? 1 : 0) + (averageSlot ? 1 : 0) + (budget === null ? 0 : 1)
            return (
              <div role="list" aria-label="Totals" className={`cf-tiles${tilesClass(tiles)} cf-tiles--${bp}`}>
                <Tile
                  label={label}
                  figure={
                    <span className="cf-tile__figure">
                      <Money cents={amount} currency={d.currency} display="whole" />
                    </span>
                  }
                  sub={sub}
                />
                {showShare && share !== null && (
                  <Tile
                    label="Share of spending"
                    figure={
                      <span className="cf-tile__figure">
                        {share === 'loading' ? <Skeleton height={22} width={60} /> : sharePercent(share.pct)}
                      </span>
                    }
                    sub={
                      share === 'loading' ? null : (
                        <>
                          of <Money cents={share.expenseCents} currency={share.currency} display="whole" /> out
                        </>
                      )
                    }
                  />
                )}
                {averageSlot && (
                  <Tile
                    label="Average transaction"
                    figure={
                      <span className="cf-tile__figure">
                        {average === null ? <Skeleton height={22} width={60} /> : <Money cents={average} currency={d.currency} display="whole" />}
                      </span>
                    }
                    sub={`${partial ? 'so far ' : ''}this ${NOUN[g]}`}
                  />
                )}
                {budget !== null && <BudgetTile budget={budget} />}
                <Tile
                  label="Transactions"
                  figure={
                    <span className="cf-tile__figure">
                      {total !== null ? (
                        total
                      ) : transactions.status === 'loading' ? (
                        <Skeleton height={22} width={40} />
                      ) : (
                        <>
                          <span aria-hidden="true">—</span>
                          <span className="sr-only">Not available</span>
                        </>
                      )}
                    </span>
                  }
                  // The figure carries the count; the sub names the period only.
                  sub={total === null ? null : `${partial ? 'so far ' : ''}this ${NOUN[g]}`}
                />
              </div>
            )
          }}
        </Async>
      </div>

      {prompt}

      <Panel className={`txn-panel cf-detail__list${phone ? ' txn-panel--flow' : ''}`}>
        <Async
          surface="transactions"
          state={listState}
          skeleton={
            <div className="txn-skeleton" aria-hidden="true">
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </div>
          }
          empty={{ title: emptyList }}
        >
          {(page) => (
            <>
              {phone && page.rows.length > 0 ? (
                <TransactionCards
                  groups={dayGroups(page.rows, {})}
                  accounts={accounts}
                  categories={categories}
                  categoriesStatus={categoriesStatus}
                  backFrom={backFrom}
                  onCategoryClick={onCategoryClick}
                  onMerchantClick={onMerchantClick}
                />
              ) : (
                <div className="txn-panel__grid">
                  <TransactionGrid
                    groups={dayGroups(page.rows, {})}
                    accounts={accounts}
                    categories={categories}
                    categoriesStatus={categoriesStatus}
                    backFrom={backFrom}
                    onCategoryClick={onCategoryClick}
                    onMerchantClick={onMerchantClick}
                    empty={<p className="txn-empty">{emptyList}</p>}
                  />
                </div>
              )}
              {(page.hasMore || page.page > 1) && (
                <Pager page={page} fetching={transactions.fetching} onPage={onPage} pageSize={SCOPED_PAGE_SIZE} />
              )}
            </>
          )}
        </Async>
      </Panel>
    </div>
  )
}

const NO_DEFICITS: ReadonlySet<LocalDate> = new Set()
